<div class="mx-auto w-full max-w-4xl rounded-xl bg-white p-4 shadow sm:p-6">
  <h2 class="mb-6 text-center text-xl font-bold sm:text-2xl">Order Tracking</h2>

  <!-- 🔄 Loading -->
  <div *ngIf="loading" class="text-center text-gray-500 py-8">
    <p>Loading tracking details...</p>
  </div>

  <!-- ❌ Error -->
  <div *ngIf="error" class="text-center text-red-500 py-8">
    <p>{{ error }}</p>
    <button
      (click)="router.navigate(['/'])"
      class="mt-2 text-blue-500 underline"
    >
      Go Back
    </button>
  </div>

  <!-- ✅ Tracking Data -->
  <div *ngIf="!loading && !error && trackingSteps.length > 0" class="space-y-8">
    <!-- 🛒 Product Details -->
    <div class="rounded-lg border bg-gray-50 p-4 sm:p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        Order Information
      </h3>
      <div class="flex min-w-0 flex-col items-start gap-4 sm:flex-row sm:gap-6">
        <!-- Product Image Placeholder -->
        <div
          class="mx-auto flex h-24 w-24 shrink-0 items-center justify-center rounded bg-gray-200 sm:mx-0"
        >
          <!-- <span class="text-gray-500 text-sm">No Image</span> -->
          <img
            [src]="imageUrl + (data[0]?.productImage || '')"
            alt="productImage"
          />
        </div>

        <!-- Product Info -->
        <div class="w-full min-w-0 flex-1">
          <h4 class="font-medium font-semibold text-gray-900 uppercase">
            {{ trackingData?.Shipment?.Consignee?.Name || "N/A" }}
          </h4>
          <h3
            class="break-words font-medium font-semibold text-gray-900 uppercase"
          >
            {{ data[0]?.productName || "N/A" }}
          </h3>
          <p class="text-sm text-gray-600 mt-1">
            <strong>AWB:</strong> {{ trackingData?.Shipment?.AWB }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>Quantity:</strong> {{ trackingData?.Shipment?.Quantity }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>Order Type:</strong> {{ trackingData?.Shipment?.OrderType }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>Invoice Amount:</strong> ₹{{
              trackingData?.Shipment?.InvoiceAmount
            }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>Order Id:</strong>
            {{ trackingData?.Shipment?.ReferenceNo }}
          </p>
          <p class="text-sm text-gray-600">
            <strong>Expected Delivery Date :</strong>
            <span class="text-green-500 font-semibold">
              {{
                trackingData?.Shipment?.Status?.Status === "Delivered"
                  ? "Delivered"
                  : (trackingData?.Shipment?.ExpectedDeliveryDate
                    | date : "MMM d, y")
              }}
            </span>
          </p>
        </div>
      </div>
    </div>

    <section class="relative overflow-hidden bg-gray-50 dark:bg-gray-900">
      <div class="mt-2 md:mt-0 py-12 pb-6 sm:py-16 lg:pb-24 overflow-hidden">
        <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 relative">
          <h3
            class="text-lg font-semibold text-gray-800 dark:text-white text-center"
          >
            Shipment Progress
          </h3>
          <div class="relative mt-12 lg:mt-20">
            <!-- Shipment Progress -->

            <!-- Curvy SVG Line (Connector) -->
            <div class="absolute inset-x-0 hidden md:block">
              <svg
                class="w-full"
                xmlns="http://www.w3.org/2000/svg"
                width="875"
                height="48"
                viewBox="0 0 875 48"
                fill="none"
              >
                <path
                  d="M2 29C20.2154 33.6961 38.9915 35.1324 57.6111 37.5555C80.2065 40.496 102.791 43.3231 125.556 44.5555C163.184 46.5927 201.26 45 238.944 45C312.75 45 385.368 30.7371 458.278 20.6666C495.231 15.5627 532.399 11.6429 569.278 6.11109C589.515 3.07551 609.767 2.09927 630.222 1.99998C655.606 1.87676 681.208 1.11809 706.556 2.44442C739.552 4.17096 772.539 6.75565 805.222 11.5C828 14.8064 850.34 20.2233 873 24"
                  stroke="#D4D4D8"
                  stroke-width="4"
                  stroke-linecap="round"
                  stroke-dasharray="1 12"
                />
              </svg>
            </div>

            <!-- Shipment Progress Steps -->
            <div
              class="relative grid grid-cols-1 gap-x-6 gap-y-8 text-center sm:gap-y-10 md:grid-cols-4 md:gap-y-12"
            >
              <ng-container *ngFor="let step of trackingSteps; let i = index">
                <div>
                  <!-- Step Circle -->
                  <div
                    class="flex items-center justify-center w-16 h-16 mx-auto rounded-full shadow font-bold text-white text-lg relative z-10"
                    [ngClass]="{
                      'bg-green-500':
                        step.status === 'completed' ||
                        step.status === 'current',
                      'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400':
                        step.status === 'pending'
                    }"
                  >
                    <!-- Show checkmark for 'completed' or 'current' -->
                    <span
                      *ngIf="
                        step.status === 'completed' || step.status === 'current'
                      "
                    >
                      ✓
                    </span>

                    <!-- Show step number only for 'pending' -->
                    <span *ngIf="step.status === 'pending'">
                      {{ i + 1 }}
                    </span>
                  </div>

                  <!-- Label -->
                  <h3
                    class="mt-4 sm:mt-6 text-lg font-semibold leading-tight text-gray-900 dark:text-white md:mt-10"
                  >
                    {{ step.label }}
                  </h3>

                  <!-- Date (for completed or current step) -->
                  <p
                    class="mt-2 text-xs text-gray-500"
                    *ngIf="
                      (step.status === 'completed' ||
                        step.status === 'current') &&
                      step.date
                    "
                  >
                    {{ step.date | date : "MMM d, y, h:mm a" }}
                  </p>

                  <!-- Location (for completed or current step) -->
                  <p
                    class="text-xs text-gray-400"
                    *ngIf="
                      (step.status === 'completed' ||
                        step.status === 'current') &&
                      step.location
                    "
                  >
                    {{ step.location }}
                  </p>
                </div>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 📦 Shipment Summary -->
    <div class="border-t pt-6 mt-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Shipment Summary</h3>
      <table class="w-full text-sm text-left border border-gray-300 rounded">
        <tr>
          <td class="font-medium px-4 py-2 border-b">Status</td>
          <td class="px-4 py-2 border-b">
            <span
              class="inline-block px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800"
            >
              {{ trackingData?.Shipment?.Status?.Status }}
            </span>
          </td>
        </tr>
      </table>
    </div>
  </div>

  <!-- 🚫 No Tracking Events -->
  <div
    *ngIf="!loading && !error && !trackingSteps.length"
    class="text-center text-gray-500 py-8"
  >
    <p>No tracking events found for this shipment.</p>
  </div>
</div>
